// Freight / weight-dims request composer — THE ONLY ONE. Opened from the Deal Dashboard's
// Purchasing card AND from the Quote Workstation's freight panel; both hand it rows + a vendor
// and get the universal compose window (RQD.openComposeModal) with the editable items table.
//
// CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — the Workstation used to render its own
// window (.rq-ws-email-modal: flat inputs, a markdown body, a separate Items pane) and the two
// surfaces looked nothing alike for the same ask. Ryan: *"I want them both to use the DD one...
// but we may need to continue using that editable items block the QW has."* So the QW's window
// is gone and the one thing it did better — sub-description toggle chips, which is how roll
// lengths and colours get included or struck out — lives in buildItemsTableHtml here.
//
// Exposes:  window.DashboardFreightModal.open(opts)
//   opts = {
//     quoteRef:        '#1663' / 'RW-1059-A' — used as the lifecycle key
//     vendorName:      'ECORE Athletic' (or whatever Shopify product.vendor)
//     type:            'freight' (quote ask) | 'dims' (weight & dims ask)
//     items:           [{ qtyStr, title, sku, subLines: [],
//                         subDescChoices: [{ text, included }],  — optional; renders as chips
//                         _vendor: 'TKO' }]                      — optional; multi-vendor prefix
//     shipToParts/shipVia/addOnsLines/zip — block content (freight asks only)
//     vendorFreight:   /vendor-freight-email payload — per-vendor copy, recipients, and poFrom
//     vendorRecipients:{ email, cc, bcc }   — remembered contacts; the profile overrides them
//     bucket:          shipping-bucket id (Deal Dashboard only; the QW has no buckets)
//     onSent:          callback after a successful send (receives { quoteRef, vendorName,
//                      requestedAt, scheduledFor }) so the caller can refresh its UI.
//   }
//
// On send: POSTs to /freight-email which (a) sends the email THROUGH the sending mailbox's own
// Outlook (Graph), so it files in that mailbox's Sent Items, falling back to Resend when the
// mailbox is not connected — or QUEUES it when the ⏱ box is ticked outside business hours — and
// (b) stamps `lifecycle:{quoteRef}.freightQuotes[vendorName].requestedAt`.
// CUSTOM_SENTITEMS_BY_CLAUDE (Ryan 2026-08-28): the fallback is reported, never silent — see
// RQD.transportNote. Ryan sent a weight/dims ask as Abby and it was nowhere in her Sent Items,
// because Resend hands the message straight to the recipient and Microsoft never sees it.
(function () {
  'use strict';
  var state = null;       // { quoteRef, vendorName, items, shipTo, onSent }
  var API = null;         // resolved lazily from window.RepPortal.API

  /* FREIGHT-COMPOSE-TWIN:BEGIN
     ─────────────────────────────────────────────────────────────────────────────
     CUSTOM_FREIGHTTWIN_BY_CLAUDE (AIOPS suggestion 6743fdf255d8, Ryan 2026-09-03)

     ⛔ EVERYTHING BETWEEN THESE MARKERS EXISTS TWICE, ON PURPOSE, AND A TEST PROVES THE TWO
     COPIES ARE IDENTICAL. The other copy is `src/email/freight-request-compose.js` in the
     rfq-worker; `tests/freight-compose-parity.test.mjs` extracts THIS block, evaluates it, and
     asserts both return the same string for every fixture. Drift fails there instead of in a
     supplier's inbox. Same pattern, and the same reason, as the CONTACTS-TWIN block in
     custom-rep-portal.js.

     ⛔ WHY A TWIN AND NOT ONE IMPLEMENTATION. Ryan's requirement on the card is 100% certainty
     that an AI agent's freight request is *the email this button sends* — same subject, same
     recipients, same body HTML. Until now the email was composed only here, client-side, so an
     agent had no choice but to write its own; that is precisely what the card forbids. The theme
     has no build system and cannot import a worker module, so "one rule, two runtimes" is the
     only shape available, and the parity test is what makes it safe.

     ⛔ NOTHING IMPURE MAY ENTER THIS SPAN — no DOM, no sessionStorage, no window, no fetch. The
     parity test evaluates the block with `new Function`, where any of those throw. `escHtml`'s
     entry to `buildItemsTableHtml` reads localStorage inside a try/catch and only on the
     `editable` path, which the server never takes.

     ⚠️ THE ONE SEAM THE TWIN CANNOT CROSS is Quill. `buildFreightRequestEmail` composes from the
     prefill body directly; the human path composes from what Quill hands back after rendering
     that same prefill. For an untouched canvas those are the same string — Quill stores a line as
     `<p>…</p>` and a blank as `<p><br></p>`, which is exactly the shape `buildPrefillBodyHtml`
     emits — and the moment a rep types, the two are *supposed* to differ. An agent has no canvas,
     so it always gets the untouched render. Stated here rather than assumed silently.
     ───────────────────────────────────────────────────────────────────────────── */
  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Items table (HTML) — matches the Workstation's buildItemsTableHtml.
  //    11pt Calibri, sized to content, max-width:100% so it can't overflow
  //    the card. Each row: SKU · Description · Qty.
  //
  // opts.editable=true renders the qty value inside a contenteditable
  //   <span data-item-idx> so admin can adjust quantities in the modal
  //   before sending. The editable variant is for in-modal display only —
  //   the version that goes out in the email body is always built with
  //   opts.editable=false (clean, no edit cues).
  /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — one chip renderer, called from the
     table build AND from the click handler that re-renders a single row's cluster. Two copies of
     this markup is how the Workstation's version drifted from its own re-render. */
  function subDescChipHtml(itemIdx, sdIdx, choice) {
    var on = !!(choice && choice.included);
    var style = on
      ? 'background:#dcfce7;border:1px solid #86efac;color:#14532d;'
      : 'background:#f3f4f6;border:1px solid #d1d5db;color:#6b7280;text-decoration:line-through;';
    return '<button type="button" class="rq-ws-email-modal__subdesc-toggle"'
      + ' data-item-idx="' + itemIdx + '" data-sd-idx="' + sdIdx + '"'
      + ' aria-pressed="' + on + '"'
      + ' title="' + (on ? 'Click to exclude from email' : 'Click to include in email') + '"'
      + ' style="' + style + 'font-family:inherit;font-size:11px;padding:2px 8px;border-radius:999px;'
      + 'margin:2px 4px 2px 0;cursor:pointer;line-height:1.4;">'
      + (on ? '✓ ' : '＋ ') + escHtml(choice && choice.text)
      + '</button>';
  }

  function buildItemsTableHtml(rows, opts) {
    var editable = !!(opts && opts.editable);
    var primaryVendorName = (opts && opts.vendorName) || '';
    // Multi-vendor only when the rows actually disagree — a single-supplier ask must not carry
    // a prefix repeating the vendor the email is already addressed to.
    var _vendorSet = {};
    rows.forEach(function (r) { _vendorSet[r._vendor || primaryVendorName] = true; });
    var multiVendor = Object.keys(_vendorSet).length > 1;
    // Editable cells render in-modal only (the sent email always uses the
    // non-editable, black-on-white variant). The modal often sits on the dark
    // theme, so lighten the editable values + underline there so they pop;
    // keep near-black in light mode. Theme read matches the portal convention.
    var darkMode = false;
    try { darkMode = localStorage.getItem('rep_portal_theme') === 'dark'; } catch (e) {}
    var editColor = darkMode ? '#dce4ee' : '#1f2937';
    var editUnderline = darkMode ? '#6b7686' : '#b9c2cf';
    // Editable cell — a contenteditable span carrying its row index + the
    // state.items field it writes back to (sku / title / qtyStr). Dashed
    // underline + min-width so empty cells stay clickable and the affordance
    // is visible. The handler that syncs these lives in open().
    function editSpan(field, idx, text, minCh, alignRight) {
      return '<span class="rq-ws-email-modal__items-edit" data-item-idx="' + idx + '"'
        + ' data-field="' + field + '" contenteditable="true" spellcheck="false"'
        + ' style="display:inline-block;min-width:' + minCh + 'ch;color:' + editColor + ';'
        + (alignRight ? 'text-align:right;' : '')
        + 'border-bottom:1px dashed ' + editUnderline + ';outline:none;">' + text + '</span>';
    }
    var h = '<table style="margin:0;border-collapse:collapse;max-width:100%;font-family:Calibri,Arial,Helvetica,sans-serif;font-size:11pt;color:#000;">';
    h += '<thead><tr>'
      + '<th style="text-align:left;padding:0 20px 5px 0;border-bottom:1.5px solid #ccc;">SKU</th>'
      + '<th style="text-align:left;padding:0 12px 5px 0;border-bottom:1.5px solid #ccc;">Description</th>'
      + '<th style="text-align:right;padding:0 0 5px 12px;border-bottom:1.5px solid #ccc;">Qty</th>'
      + '</tr></thead><tbody>';
    rows.forEach(function (r, i) {
      var last = i === rows.length - 1;
      var rowBorder = last ? '' : 'border-bottom:1px solid #e5e7eb;';
      /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — sub-descriptions render as
         TOGGLE CHIPS in editable mode, plain lines in the sent email. Lifted from the
         Workstation's own modal, which is the surface that had them; this composer only ever
         showed `subLines` as static grey text, so a QW rep moving here would have lost the one
         control the old window existed for — striking a roll length or a colour out of the ask.

         ⛔ THE CHIPS ARE THE EDITABLE FORM OF subLines, NOT AN EXTRA FIELD. `subDescChoices` is
         the full candidate list with an `included` flag per entry; `subLines` is the derived
         list of the ones that are on. Toggling rewrites subLines (see wireItemEdits), and the
         non-editable build below reads subLines only — so no chip chrome can ever reach the
         supplier's inbox, whatever the rep clicked. A row with no subDescChoices (every Deal
         Dashboard row today) falls through to the static rendering unchanged. */
      var sub = '';
      if (editable && r.subDescChoices && r.subDescChoices.length) {
        sub = '<div style="margin-top:4px;display:flex;flex-wrap:wrap;align-items:center;">'
          + r.subDescChoices.map(function (c, j) { return subDescChipHtml(i, j, c); }).join('')
          + '</div>';
      } else if (r.subLines && r.subLines.length) {
        sub = '<div style="font-size:12px;color:#555;margin-top:2px;">'
          + r.subLines.map(escHtml).join('<br>') + '</div>';
      }
      /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — multi-vendor prefix, also from
         the Workstation. A QW freight ask can cover several vendors at once ("ships with X"),
         and without the prefix the supplier gets one undifferentiated list. `_vendor` is absent
         on Deal Dashboard rows, so multiVendor is false there and nothing changes. */
      var titleHtml = escHtml(r.title || '');
      if (multiVendor) {
        var rowVendor = escHtml(r._vendor || primaryVendorName || '');
        titleHtml = rowVendor
          ? ('<strong>' + rowVendor + '</strong>' + (titleHtml ? ' - ' + titleHtml : ''))
          : titleHtml;
      }
      var skuCell, descCell, qtyCell;
      if (editable) {
        // All three fields editable in-modal — admin can correct a wrong SKU
        // or item name (not just qty) before the request goes to the carrier.
        skuCell  = editSpan('sku',    i, escHtml(r.sku   || ''), 6, false);
        // ⚠️ The editable title span holds the RAW title, never the vendor-prefixed markup —
        // an edit writes straight back to items[i].title, so a prefix baked in here would be
        // saved as part of the product name and then prefixed again on the next render.
        descCell = (multiVendor && (r._vendor || primaryVendorName)
                     ? '<strong>' + escHtml(r._vendor || primaryVendorName) + '</strong> - ' : '')
                 + editSpan('title', i, escHtml(r.title || ''), 14, false) + sub;
        qtyCell  = editSpan('qtyStr', i, escHtml(r.qtyStr || ''), 3, true);
      } else {
        skuCell  = r.sku ? escHtml(r.sku) : '<span style="color:#888;">—</span>';
        descCell = (titleHtml || '<span style="color:#888;">—</span>') + sub;
        qtyCell  = escHtml(r.qtyStr || '');
      }
      h += '<tr>'
        + '<td class="rq-ws-email-modal__items-sku" style="padding:3px 20px 3px 0;' + rowBorder + 'white-space:nowrap;vertical-align:top;">' + skuCell + '</td>'
        + '<td style="padding:3px 12px 3px 0;' + rowBorder + 'vertical-align:top;">' + descCell + '</td>'
        + '<td class="rq-ws-email-modal__items-qty" style="padding:3px 0 3px 12px;' + rowBorder + 'text-align:right;white-space:nowrap;vertical-align:top;">' + qtyCell + '</td>'
        + '</tr>';
    });
    h += '</tbody></table>';
    return h;
  }

  // Strip common trailing descriptor words from a vendor name for use in
  // the email subject line. "ECORE Athletic" → "ECORE", "Legend Fitness" →
  // "Legend", "Perform Better" stays as-is (no match in the strip list).
  // Only used for the subject — the body / signature / records keep the
  // full canonical name.
  function shortenVendorName(name) {
    if (!name) return '';
    var stripWords = ['Athletic', 'Athletics', 'Fitness', 'Equipment', 'USA', 'Inc', 'LLC', 'Corp', 'Co', 'Industries', 'International', 'Group'];
    var parts = String(name).trim().split(/\s+/);
    while (parts.length > 1) {
      var last = parts[parts.length - 1].replace(/[,.]$/, '');
      var hit = false;
      for (var i = 0; i < stripWords.length; i++) {
        if (stripWords[i].toLowerCase() === last.toLowerCase()) { hit = true; break; }
      }
      if (!hit) break;
      parts.pop();
    }
    return parts.join(' ');
  }

  // ── Email signature ─ same as workstation-freight.js. Logo served by the
  //    rfq-worker; data: URIs get stripped by Gmail/Outlook.
  function emailSignatureHtml() {
    return '<div style="font-size:11pt;line-height:1.4;color:#000;">'
      + '<p style="margin:0;font-family:Calibri,sans-serif;font-size:11pt;line-height:1.4;color:#000;">Kind regards,</p>'
      + '<br>'
      + '<p style="margin:0;font-family:Helvetica,Arial,sans-serif;font-size:11pt;line-height:1.4;"><strong style="color:#002060;">Ryan Walker</strong> <strong style="color:#002060;">|</strong> <span style="font-family:Helvetica,Arial,sans-serif;font-size:10pt;color:#000;">Owner</span></p>'
      + '<br>'
      + '<img src="https://rfq-worker.ryan-29b.workers.dev/email-logo.png" alt="Blue Sky Fitness Supply" width="288" style="display:block;margin:0;">'
      + '<div style="height:16px;line-height:16px;font-size:1px;">&nbsp;</div>'
      + '<p style="margin:0 0 2px;font-family:Helvetica,Arial,sans-serif;font-size:11pt;line-height:1.4;"><strong style="color:#002060;">P&nbsp;&nbsp;|</strong> <span style="font-family:Aptos,Calibri,sans-serif;font-size:11pt;color:#000;"><strong>720 515-3585</strong></span></p>'
      + '<p style="margin:0 0 2px;font-family:Helvetica,Arial,sans-serif;font-size:11pt;line-height:1.4;"><strong style="color:#002060;">E&nbsp;&nbsp;|</strong> <span style="font-family:Aptos,Calibri,sans-serif;font-size:11pt;color:#000;"><strong><a href="mailto:ryan@blueskyfitsupply.com" style="color:#000;text-decoration:none;">ryan@blueskyfitsupply.com</a></strong></span></p>'
      + '<p style="margin:0;font-family:Helvetica,Arial,sans-serif;font-size:11pt;line-height:1.4;"><strong style="color:#002060;">W |</strong> <span style="font-family:Aptos,Calibri,sans-serif;font-size:11pt;color:#000;"><strong><a href="https://www.blueskyfitsupply.com" style="color:#000;text-decoration:none;">www.blueskyfitsupply.com</a></strong></span></p>'
      + '</div>';
  }

  // Small italic gray footer line carrying the internal order ref. The
  // subject line moved to the zip code so the supplier's reply threads on
  // location instead of "SHOP-XXXX"; this line keeps the ref findable in
  // the email body for back-matching when the eventual PO lands.
  function refFooterHtml(quoteRef) {
    if (!quoteRef) return '';
    return '<div style="margin:18px 12px 0;font-family:Calibri,Arial,Helvetica,sans-serif;font-size:9pt;font-style:italic;color:#9aa3ad;">Internal ref: ' + String(quoteRef).replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</div>';
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — gold-standard built-in copy.
     Vendor-agnostic, so any vendor with no PO-Profile override inherits it and the
     standard lives in exactly one place. Keep in sync with workstation-freight.js. */
  var FREIGHT_DEFAULTS = {
    freight: {
      subject: 'Blue Sky Freight Request | {ZIP} | {REF}',
      body: 'Hi Team,\n\nPlease send a freight quote for the items listed below. **Thank you!**\n\n{SHIP_TO_BLOCK}\n\n{ADD_ONS_BLOCK}\n\n{ITEMS_BLOCK}',
    },
    dims: {
      subject: 'Blue Sky Weight & Dims Request | {ZIP} | {REF}',
      body: 'Hi Team,\n\nPlease send the packaged weight and dimensions for the items listed below. **Thank you!**\n\n{ITEMS_BLOCK}',
    },
    /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-29) - ONE email that asks for both, so a
       supplier is not mailed twice about the same shipment.
       WARNING: it keeps {SHIP_TO_BLOCK} and {ADD_ONS_BLOCK}, which the dims template deliberately
       drops. We are asking for a RATE here, and a rate needs a destination and the delivery
       conditions - building this by copying the dims template would silently lose both. */
    both: {
      subject: 'Blue Sky Freight + Weight & Dims Request | {ZIP} | {REF}',
      body: 'Hi Team,\n\nPlease send a freight quote for the items listed below, along with their packaged weight and dimensions. **Thank you!**\n\n{SHIP_TO_BLOCK}\n\n{ADD_ONS_BLOCK}\n\n{ITEMS_BLOCK}',
    },
  };

  /* The card is a width-less <table>, NOT a max-width <div>. A table with no width
     attribute shrinks to its content in every client including Outlook, which
     ignores max-width on divs and stretched these cards the full width of the
     window with no visible right edge. This used to be duplicated (correctly in
     composeFromProse, incorrectly here), which is exactly how the two drifted. */
  // ADD ONS card contents — one line per active delivery requirement (liftgate,
  // inside delivery, …). Empty list → the {ADD_ONS_BLOCK} token collapses away.
  function buildAddOnsInnerHtml(lines) {
    if (!lines || !lines.length) return '';
    return lines.map(function (l) { return escHtml(l); }).join('<br>');
  }

  function freightCard(inner) {
    return '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin:8px 12px;">'
      + '<tr><td style="border-top:1px solid #cfd8e7;border-right:1px solid #cfd8e7;border-bottom:1px solid #cfd8e7;'
      + 'border-left:3px solid #002060;border-radius:6px;background:#f5f8fc;padding:8px 16px;line-height:1.4;">'
      + inner + '</td></tr></table>';
  }

  /* Resolve the {…_BLOCK} tokens the Vendor Guide advertises into their cards.
     A token with no content collapses to nothing and takes the blank line it sat
     on with it, so the body never shows a gap or a raw token. Legacy **SECTION:**
     markers still work for anything hand-typed in the body editor. */
  function substituteFreightBlocks(bodyHtml, blocks) {
    blocks = blocks || {};
    function replaceBlock(html, token, label, inner) {
      var card = inner ? freightCard('<strong>' + label + ':</strong>' + inner) : '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-23) — swallow the WHOLE paragraph
         when the token sits alone in one, which is how Quill stores it and how every
         template writes it. The card is a <table> — block-level — so leaving the <p>
         wrapper in place makes the parser auto-close it before the table, stranding
         an empty <p><br></p> above the card: a phantom blank line in the sent email.
         An empty block takes its paragraph with it, so an unused token leaves no gap
         either. */
      var pRe = new RegExp('<p\\b[^>]*>\\s*(?:<br\\s*/?>\\s*)*\\{' + token + '\\}\\s*(?:<br\\s*/?>\\s*)*<\\/p>', 'i');
      if (pRe.test(html)) return html.replace(pRe, function () { return card; });
      // Inline fallback — token sits mid-paragraph alongside other text.
      var re = new RegExp('(?:<br\\s*/?>\\s*)*\\{' + token + '\\}', 'i');
      if (!re.test(html)) return html;
      return html.replace(re, function () { return card ? '<br>' + card : ''; });
    }
    /* The two vocabularies are mutually exclusive per body, and they have to be:
       the token pass emits "<strong>ITEMS:</strong>" as the card's own heading, so
       running the legacy marker pass afterwards would match that heading and wrap
       the card in a second card. Tokens win when present. */
    if (/\{(?:SHIP_TO|ADD_ONS|ITEMS)_BLOCK\}/i.test(bodyHtml)) {
      bodyHtml = replaceBlock(bodyHtml, 'SHIP_TO_BLOCK', 'SHIP TO', blocks.shipToHtml);
      bodyHtml = replaceBlock(bodyHtml, 'ADD_ONS_BLOCK', 'ADD ONS', blocks.addOnsHtml);
      bodyHtml = replaceBlock(bodyHtml, 'ITEMS_BLOCK', 'ITEMS', blocks.itemsHtml);
    } else {
      ['SHIP TO', 'SHIP VIA', 'ADD ONS'].forEach(function (label) {
        var re = new RegExp('<strong>' + label + ':<\\/strong>([\\s\\S]+?)(?:<br><br>|$)');
        bodyHtml = bodyHtml.replace(re, function (_m, inner) {
          return freightCard('<strong>' + label + ':</strong>' + inner) + '<br>';
        });
      });
      if (blocks.itemsHtml && bodyHtml.indexOf('<strong>ITEMS:</strong>') !== -1) {
        bodyHtml = bodyHtml.replace(/<strong>ITEMS:<\/strong>(<br>)?/, function () {
          return freightCard('<strong>ITEMS:</strong>' + blocks.itemsHtml) + '<br>';
        });
      }
    }
    // Safety net — a body that referenced the items nowhere still gets them.
    if (blocks.itemsHtml && bodyHtml.indexOf(blocks.itemsHtml) === -1) {
      bodyHtml += freightCard('<strong>ITEMS:</strong>' + blocks.itemsHtml) + '<br>';
    }
    return bodyHtml;
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — Phase 1: this modal no longer renders its
     own window. It opens the UNIVERSAL compose modal (RQD.openComposeModal): the editable
     items table is a `preStep` (same shell), the rendered items table is a `previewBlock`
     in the white canvas, the supplier rep signature renders via `signatureHtml`, and
     `sendOverride` composes prose + items + signature and POSTs the existing /freight-email.
     Covers both email types (freight quote + weight/dims). No worker change. */
  // Compose the full email from the composer's Quill prose + the items table + signature.
  // Flatten every Quill <p> to margin:0 (mirrors the worker's normalizeQuillHtml). Without
  // this, raw Quill paragraphs inherit the email client's ~1em <p> margins → each line is
  // tall with whitespace and the empty <p><br></p> blank lines double up. With margin:0, a
  // typed line is one tight line and a <p><br></p> is exactly ONE clean blank line.
  function normalizeQuillMargins(html) {
    if (!html || typeof html !== 'string') return html || '';
    return html.replace(/<p\b([^>]*)>/gi, function (_, attrs) {
      attrs = attrs || '';
      var m = attrs.match(/\sstyle\s*=\s*"([^"]*)"/i);
      if (m) return '<p' + attrs.replace(/\sstyle\s*=\s*"([^"]*)"/i, ' style="margin:0;' + m[1] + '"') + '>';
      return '<p' + attrs + ' style="margin:0;">';
    });
  }

  function composeFromProse(proseHtml, itemsHtml, postHtml, quoteRef, sigHtml, shipToHtml, addOnsHtml) {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — the block cards are now placed
       by the {…_BLOCK} tokens in the body, so a vendor template controls WHERE each
       one lands. The card builder itself moved to the shared freightCard() so this
       file can't drift from the one composeFreightEmailHtml uses again. */
    var prose = normalizeQuillMargins(proseHtml || '');
    // Legacy bodies — no tokens and no markers — keep the historical layout: SHIP
    // TO, then ADD ONS, then ITEMS, appended after the prose. Expressing it as
    // appended tokens means there's only one placement code path to reason about.
    if (!/\{(?:SHIP_TO|ADD_ONS|ITEMS)_BLOCK\}/i.test(prose)
        && prose.indexOf('<strong>SHIP TO:</strong>') === -1
        && prose.indexOf('<strong>ITEMS:</strong>') === -1) {
      if (shipToHtml && String(shipToHtml).trim()) prose += '{SHIP_TO_BLOCK}';
      if (addOnsHtml && String(addOnsHtml).trim()) prose += '{ADD_ONS_BLOCK}';
      prose += '{ITEMS_BLOCK}';
    }
    var h = '<div style="font-family:Calibri,Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.4;color:#000;max-width:640px;">';
    h += '<div style="margin:0 0 16px;">' + substituteFreightBlocks(prose, {
      shipToHtml: shipToHtml,
      addOnsHtml: addOnsHtml,
      itemsHtml: itemsHtml,
    }) + '</div>';
    // Optional post-table note the rep typed below the items in the canvas.
    if (postHtml && String(postHtml).replace(/<[^>]*>/g, '').trim()) {
      h += '<div style="margin:14px 12px 0;">' + postHtml + '</div>';
    }
    // One clean blank line before the signature — same shape as buildEmailHtml's
    // universal body→signature break, so every email type matches.
    h += '<p style="margin:0;font-family:Calibri,Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.4;color:#000;"><br></p>';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — signature comes from the From dropdown's
       selected identity (passed as args.fromSignatureHtml → the SAME rep/Abby signature the
       preview shows, so preview == sent). Falls back to the local rep signature if absent. */
    h += (sigHtml && String(sigHtml).trim()) ? sigHtml : emailSignatureHtml();
    h += refFooterHtml(quoteRef);
    h += '</div>';
    return h;
  }

  // CUSTOM_RFQPORTAL_BY_CLAUDE — fill {VENDOR}/{ZIP}/{REF} placeholders in
  // per-vendor freight-request verbiage (Edit Vendor → PO Profile).
  function renderFreightVars(tpl, vars) {
    return String(tpl || '').replace(/\{(VENDOR|ZIP|REF)\}/gi, function (_m, k) {
      var v = vars[k.toUpperCase()];
      return v == null ? '' : String(v);
    });
  }

  // CUSTOM_RFQPORTAL_BY_CLAUDE — render the ship-to address as a neat table
  // (mirrors the items table) instead of a flat prose line. Non-editable.
  function buildShipToTableHtml(parts, shipVia) {
    parts = parts || {};
    var pairs = [['Location', parts.location], ['Type', parts.type], ['Ship Via', shipVia]].filter(function (p) { return p[1]; });
    if (!pairs.length) return '';
    // First row carries a cell-level top rule (Outlook-safe — same technique as the
    // ITEMS table's header underline) so the card reads as a sibling of ITEMS.
    var rows = pairs.map(function (p, i) {
      var rule = i === 0 ? 'border-top:1.5px solid #ccc;' : '';
      var padTop = i === 0 ? '5px' : '3px';
      return '<tr>'
        + '<td style="text-align:left;padding:' + padTop + ' 20px 3px 0;color:#666;white-space:nowrap;vertical-align:top;' + rule + '">' + escHtml(p[0]) + '</td>'
        + '<td style="text-align:left;padding:' + padTop + ' 0 3px;' + rule + '">' + escHtml(p[1]) + '</td>'
        + '</tr>';
    }).join('');
    return '<table style="margin:0;border-collapse:collapse;font-family:Calibri,Arial,Helvetica,sans-serif;font-size:11pt;color:#000;"><tbody>' + rows + '</tbody></table>';
  }

  /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — WHO this ask is addressed to, in one
     place. This lived inside the Deal Dashboard's caller (dashboard-handshake.js), which meant the
     Quote Workstation — now opening this same composer — had no version of it at all and would
     have fallen back to whatever `vendor-contacts` last remembered, ignoring the Vendor Guide's
     dedicated freight recipients entirely.

     The precedence, unchanged from the DD's copy:
       To  : this type's profile override → the OTHER type's override → the PO contact email
             → whatever vendor-contacts remembered.
       Cc  : the profile override (this type's, else the other type's), ADDED to the remembered
             list (not replacing it — a remembered Cc is somebody a human put there).
             ⛔ NEVER THE PO CONTACT'S CC (Ryan 2026-09-06, Spirit Fitness). The Guide's top
             "CC Emails" belongs to the PURCHASE ORDER; a freight ask with an empty freight Cc used
             to inherit it, so Nick Dihigo was copied on a rate request he has nothing to do with.
             The freight / dims sections own their own Cc. Only the To still falls back to the PO
             contact email, because a vendor with no freight address at all must still be reachable.
       Bcc : a profile override REPLACES a remembered value, but a BLANK profile does NOT clear
             one — `rec` starts as a copy of vendorRecipients (the `vendor-contacts` memory), so a
             remembered Bcc survives the `if (vpBcc)` below untouched.
             ⛔ THAT FALLBACK WAS NEVER INTENDED, and this comment used to deny it existed ("no
             fallback"), which is what made it hard to find. It silently overrode the Guide on
             Perform Better (found 2026-08-29): `vendor-contacts` still held a ryan@ written back
             by a send made BEFORE the hardcoded self-Bcc was removed on 2026-08-27, so a profile
             reading Bcc-none composed with a Bcc anyway. The value outlived the code that made it.
             It could not self-clear because sendOverride used to OMIT `bcc` from the payload when
             the box was empty, and the worker's write-back is guarded on `body.bcc !== undefined`.
             sendOverride now always posts `bcc`, so emptying the box really does clear the memory.
             ⚠️ Most profiles carry none, so a vendor email with no Bcc goes out with no copy to
             anybody — the deliberate trade made when the hardcoded self-Bcc was removed.

     ⛔ THE CROSS-TYPE FALLBACK IS LOAD-BEARING: filling in EITHER the freight or the dims section
     of a PO Profile is meant to cover both buttons. Dropping it would silently blank the To on
     every vendor that only ever had one of the two filled in. */
  function mergeVendorRecipients(vendorRecipients, vendorFreight, emailType) {
    var rec = Object.assign({}, vendorRecipients || {});
    var fv = vendorFreight || {};
    var isDims = emailType === 'dims';
    var split = function (s) { return String(s || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean); };
    var vpTo  = (isDims ? fv.freightDimsTo  : fv.freightQuoteTo)  || fv.freightDimsTo  || fv.freightQuoteTo  || '';
    var vpCc  = (isDims ? fv.freightDimsCc  : fv.freightQuoteCc)  || fv.freightDimsCc  || fv.freightQuoteCc  || '';
    var vpBcc = (isDims ? fv.freightDimsBcc : fv.freightQuoteBcc) || fv.freightDimsBcc || fv.freightQuoteBcc || '';
    var resolvedTo = vpTo || fv.contactEmail || '';
    if (resolvedTo) rec.email = resolvedTo;
    var resolvedCc = vpCc;
    if (resolvedCc) {
      var existingCc = Array.isArray(rec.cc) ? rec.cc.slice() : (rec.cc ? split(rec.cc) : []);
      rec.cc = existingCc.concat(split(resolvedCc).filter(function (e) { return existingCc.indexOf(e) < 0; }));
    }
    if (vpBcc) rec.bcc = split(vpBcc);
    return rec;
  }
  /* END CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE */

  /* CUSTOM_FREIGHTTWIN_BY_CLAUDE (Ryan 2026-09-03) — the three lifts below came OUT of open().
     They are the last pure steps that only existed inside it, and while they lived there the
     server had nothing to share: an agent could reach the send route and still had to invent the
     subject and the greeting. open() now calls them, so they are genuinely this button's code and
     not a second implementation written to look like it. */

  /** The {VENDOR}/{ZIP}/{REF} substitutions, from the three facts a deal supplies. */
  function freightVarsFor(vendorName, zip, quoteRef) {
    return { VENDOR: shortenVendorName(vendorName), ZIP: (zip || ''), REF: (quoteRef || '') };
  }

  /** The RAW (unrendered) subject + body for this ask: the vendor's PO-Profile override when it
      has one, else the gold-standard built-in. ⚠️ An EMPTY profile field is the normal, intended
      state — it means "inherit the standard", not "unconfigured". Legend Fitness carries none and
      that is what produced the reference send this composer is measured against. */
  function freightTemplateFor(vendorFreight, emailType) {
    var vf = vendorFreight || {};
    var customSubject = (emailType === 'dims' ? (vf.freightDimsSubject || '') : (vf.freightQuoteSubject || '')).trim();
    var customBody = (emailType === 'dims' ? (vf.freightDimsBody || '') : (vf.freightQuoteBody || '')).trim();
    /* 'both' has its own template; anything unrecognised still falls back to freight. */
    var defaults = FREIGHT_DEFAULTS[emailType] || FREIGHT_DEFAULTS.freight;
    /* CUSTOM_VENDORGREET_BY_CLAUDE (AIOPS 0c175f74a4f9) — the Guide's order-processing first name
       ("Hi Carlene,") replaces a body's opening "Hi Team,"; a body that opens any other way is left
       as typed. `greetingName` rides on vendorFreight from the profile (worker) / the JSON (theme). */
    return { subject: customSubject || defaults.subject, body: greetFreightBody(customBody || defaults.body, vf.greetingName) };
  }

  /** Swap an opening "Hi Team," for "Hi {name},". No name, or any other opener → unchanged. */
  function greetFreightBody(body, greetingName) {
    var name = String(greetingName || '').trim();
    var s = String(body || '');
    if (!name) return s;
    return s.replace(/^(\s*)Hi Team,/i, '$1Hi ' + name + ',');
  }

  /* CUSTOM_ASKCLAUSE_BY_CLAUDE (AIOPS `317cf637f554`, Ryan 2026-09-11) — A VENDOR'S QUIRK LINE
     BELONGS INSIDE THE ASK, NEVER ABOVE THE GREETING.
     ────────────────────────────────────────────────────────────────────────
     SHOP-2050 went to ECORE reading:

         If possible, please source from Corona, CA.

         Hi Team,
         Please send a freight quote for the items listed below. Thank you!

     — an instruction fired at a supplier before we have said hello. `getVendorFreightExtras`
     returns per-vendor lines and this function simply put them FIRST, which is the only
     placement that is wrong for every one of them.

     ⛔ THE FIX IS NOT "PUT THEM LAST". A second paragraph after the ask reads as an
     afterthought and the supplier's eye has already left the sentence that matters. Ryan wrote
     the target himself: the preference belongs in the ASK SENTENCE, joined with "and".

         Hi Team,
         Please send a freight quote for the items listed below, and if possible, please source
         from Corona, CA. Thank you!

     ⚠️ A QUIRK LINE IS THEREFORE WRITTEN AS A CLAUSE, not as a sentence — it is lowercased
     and loses its full stop when woven in. Reword it where it is declared
     (dashboard-handshake.js, FREIGHT-INPUTS-TWIN) if it would not read correctly both ways.

     ⚠️ AND THE FALLBACK STILL NEVER LEADS. A vendor's own Guide body is free text an admin
     types and may have no sentence to weave into. Then the line becomes its own paragraph —
     but AFTER the greeting, because "no ask sentence to join" is not a reason to go back to
     speaking before hello. */

  /** A line that is the ask: it opens with "Please" and ends a sentence somewhere in it. */
  function isAskLine(line) {
    var s = String(line == null ? '' : line);
    return /^\s*please\b/i.test(s) && s.indexOf('.') !== -1;
  }

  /** "If possible, please …" → "if possible, please …". An ALL-CAPS opener is left alone. */
  function clauseCase(clause) {
    var s = String(clause == null ? '' : clause).trim().replace(/\.\s*$/, '');
    var first = s.split(/\s+/)[0] || '';
    /* Lower the opener only when it is an ordinary capitalised word. `ECORE`, `CA`, `LTL` keep
       their case: lowering an acronym is a visible error, and leaving a word capitalised
       mid-sentence is a smaller one. */
    if (!/^[A-Z][a-z]/.test(first)) return s;
    return s.charAt(0).toLowerCase() + s.slice(1);
  }

  /* Fold the vendor's quirk lines into the ask sentence. Pure; returns a new array.
     ⛔ THE SPLIT IS ON THE FIRST FULL STOP, so whatever the template hangs after the ask
     ("**Thank you!**") stays hanging after it. Appending to the end of the line instead would
     have produced "… below. Thank you! and if possible …". */
  function weaveAskClauses(bodyLines, clauses) {
    var lines = (bodyLines || []).slice();
    var list = (Array.isArray(clauses) ? clauses : [])
      .map(function (c) { return String(c == null ? '' : c).trim(); })
      .filter(Boolean);
    if (!list.length) return lines;
    var joined = list.map(clauseCase).join(', and ');

    var askAt = -1;
    for (var i = 0; i < lines.length; i++) { if (isAskLine(lines[i])) { askAt = i; break; } }
    if (askAt !== -1) {
      var line = String(lines[askAt]);
      var dot = line.indexOf('.');
      lines[askAt] = line.slice(0, dot) + ', and ' + joined + line.slice(dot);
      return lines;
    }

    /* No sentence to join — a paragraph of its own, under the greeting. */
    var at = 0;
    for (var j = 0; j < lines.length; j++) {
      var t = String(lines[j]).trim();
      if (t && /,$/.test(t)) { at = j + 1; break; }
    }
    var block = [''].concat(list);
    var next = lines[at];
    if (next !== undefined && String(next).trim() !== '') block.push('');
    lines.splice.apply(lines, [at, 0].concat(block));
    return lines;
  }

  /* CUSTOM_PRIORDIMS_BY_CLAUDE (AIOPS 72635be15403, Ryan 2026-09-28) — the freight ask to a supplier
     who already sent weight & dims. `mode` comes from `priorDimsFor` (FREIGHT-INPUTS-TWIN):
     'compare' puts Ryan's sentence straight after the ask sentence; 'thanks' puts the thank-you on
     the line above the ask and adds "as well" to it. Woven BEFORE the vendor's quirk clauses. No ask
     sentence → its own paragraph under the greeting. Any other mode → the lines, untouched.
     ⛔ TWIN of src/email/freight-request-compose.js — edit both or the parity test goes red. */
  var PRIOR_DIMS_COMPARE = "Our carrier came back much more expensive than we'd like, so we wanted to compare to your rates.";
  var PRIOR_DIMS_THANKS = 'Thank you for the weight and dimensions.';
  function weavePriorDims(bodyLines, mode) {
    var lines = (bodyLines || []).slice();
    if (mode !== 'compare' && mode !== 'thanks') return lines;
    var askAt = -1;
    for (var i = 0; i < lines.length; i++) { if (isAskLine(lines[i])) { askAt = i; break; } }
    if (askAt !== -1) {
      var line = String(lines[askAt]);
      var dot = line.indexOf('.');
      if (mode === 'compare') {
        lines[askAt] = line.slice(0, dot + 1) + ' ' + PRIOR_DIMS_COMPARE + line.slice(dot + 1);
      } else {
        lines[askAt] = line.slice(0, dot) + ' as well' + line.slice(dot);
        lines.splice(askAt, 0, PRIOR_DIMS_THANKS);
      }
      return lines;
    }
    var at = 0;
    for (var j = 0; j < lines.length; j++) {
      var t = String(lines[j]).trim();
      if (t && /,$/.test(t)) { at = j + 1; break; }
    }
    var block = ['', mode === 'compare' ? PRIOR_DIMS_COMPARE : PRIOR_DIMS_THANKS];
    var next = lines[at];
    if (next !== undefined && String(next).trim() !== '') block.push('');
    lines.splice.apply(lines, [at, 0].concat(block));
    return lines;
  }
  /* END CUSTOM_PRIORDIMS_BY_CLAUDE */

  /* CUSTOM_FEDEXPRIORITYASK_BY_CLAUDE (ONYX 7111f63fcdc3, Ryan 2026-10-13, TEST 214 / SHOP-2168) - THE ASK SAYS
     FEDEX PRIORITY OVERNIGHT WHEN THE CUSTOMER PAID FOR IT. ECORE sometimes reads past the table's
     "Ship Via: FedEx Priority" and quotes FedEx Ground. So when the ship-via is FedEx Priority, the ask
     sentence's "freight quote" becomes "**__FEDEX PRIORITY OVERNIGHT__** quote" (bold + underlined; Ryan
     asked for the word OVERNIGHT). The table, Ship Via line included, is untouched. Mixed shipping (some
     Ground, some Priority) starts "Mixed shipping", so it is not this rule: its own Ship Via line asks.
     A rate ask only (freight and the combined freight + dims ask); a dims ask has no ship-via. An ask
     line with no "quote" in it (a Guide body typed another way) is left as typed. */
  var FEDEX_PRIORITY_ASK = '**__FEDEX PRIORITY OVERNIGHT__**';
  function isFedexPriorityShipVia(shipVia) {
    return /^\s*fedex\s+priority\b/i.test(String(shipVia == null ? '' : shipVia));
  }
  function weavePriorityAsk(bodyLines, shipVia) {
    var lines = (bodyLines || []).slice();
    if (!isFedexPriorityShipVia(shipVia)) return lines;
    for (var i = 0; i < lines.length; i++) {
      if (!isAskLine(lines[i])) continue;
      lines[i] = String(lines[i]).replace(/\b(?:freight\s+)?quote\b/i, FEDEX_PRIORITY_ASK + ' quote');
      break;
    }
    return lines;
  }
  /* END CUSTOM_FEDEXPRIORITYASK_BY_CLAUDE */

  /* CUSTOM_PARCELASK_BY_CLAUDE (ONYX d4ef3836f115, Ryan 2026-10-16 - RW-1507-A) - A PARCEL-SIZED ASK SAYS SO IN
     THE ASK SENTENCE ITSELF, FOR EVERY SUPPLIER. Ryan's words: "Please send a parcel shipment quote for the item[s]
     listed below. Please do not quote LTL. Thank you!" So the ask line's "(freight) quote" becomes "parcel shipment
     quote", "the items" becomes "the item" for a one-line ask, and "Please do not quote LTL." goes right after the
     first full stop, before whatever the template hangs there ("Thank you!"). No "Thank you" is added: the template
     already carries it. Rate asks only (freight / both; a dims ask has no rate), and never on a FedEx Priority
     Overnight ship-via (the customer paid for overnight). An ask line with no "quote" in it (a Guide body typed
     another way) keeps its words and gains PARCEL_NO_QUOTE_LINE after its first sentence. No ask line at all ->
     the parcel sentence is its own paragraph under the greeting. TWIN of src/email/freight-request-compose.js. */
  var PARCEL_NO_LTL = 'Please do not quote LTL.';
  var PARCEL_NO_QUOTE_LINE = 'Please quote a parcel shipment, not LTL.';
  function parcelAskSentence(itemCount) {
    return 'Please send a parcel shipment quote for the ' + (Number(itemCount) === 1 ? 'item' : 'items') + ' listed below. ' + PARCEL_NO_LTL;
  }
  function weaveParcelAsk(bodyLines, parcel, itemCount, shipVia) {
    var lines = (bodyLines || []).slice();
    if (!parcel || isFedexPriorityShipVia(shipVia)) return lines;
    for (var i = 0; i < lines.length; i++) {
      if (!isAskLine(lines[i])) continue;
      var line = String(lines[i]);
      var dot = line.indexOf('.');
      var first = line.slice(0, dot);
      var rest = line.slice(dot + 1);
      if (/\bquote\b/i.test(first)) {
        first = first.replace(/\b(?:freight\s+)?quote\b/i, 'parcel shipment quote');
        if (Number(itemCount) === 1) first = first.replace(/\bthe items\b/i, 'the item');
        lines[i] = first + '. ' + PARCEL_NO_LTL + rest;
      } else {
        lines[i] = first + '. ' + PARCEL_NO_QUOTE_LINE + rest;
      }
      return lines;
    }
    var at = 0;
    for (var j = 0; j < lines.length; j++) {
      var t = String(lines[j]).trim();
      if (t && /,$/.test(t)) { at = j + 1; break; }
    }
    var block = ['', parcelAskSentence(itemCount)];
    var next = lines[at];
    if (next !== undefined && String(next).trim() !== '') block.push('');
    lines.splice.apply(lines, [at, 0].concat(block));
    return lines;
  }
  /* END CUSTOM_PARCELASK_BY_CLAUDE */

  /** The Quill seed: the template body with the {…_BLOCK} tokens stripped (they are redundant
      in this surface — see the note lifted with the code), the vendor's quirk lines woven into
      its ask sentence. */
  function buildPrefillBodyHtml(bodyTemplate, vars, extraBodyLines, priorDims, shipVia, parcel, itemCount) { // parcel, itemCount: CUSTOM_PARCELASK_BY_CLAUDE
    // Items + SHIP TO/SHIP VIA are NOT in the prose — they ride as fixed blocks and get
    // composed into the sent email.
    /* The body — per-vendor or gold-standard default — owns its own greeting.
       CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-23) — strip the {…_BLOCK} tokens from
       the COMPOSER seed. In this surface they're redundant: SHIP TO, ADD ONS and the
       (editable) items table each render as their OWN block in the white canvas, so a
       raw token in the editor is just noise — visible to the rep, and deletable by
       accident. With no tokens left in the prose, composeFromProse's legacy path
       appends the blocks in the documented order (SHIP TO → ADD ONS → ITEMS), which is
       exactly what the canvas already shows, so composer and sent email agree.
       Tokens still drive placement server-side and in the Workstation. */
    var _blockTokenRe = /\{(?:SHIP_TO|ADD_ONS|ITEMS)_BLOCK\}/gi;
    var _bodyLines = renderFreightVars(bodyTemplate, vars)
      .split(/\r?\n/)
      // Drop lines that are nothing BUT block tokens — otherwise removing the token
      // would leave the blank line it sat on, which is the gap we just fixed downstream.
      .filter(function (l) {
        var stripped = String(l).replace(_blockTokenRe, '');
        return !(stripped !== String(l) && stripped.trim() === '');
      })
      // A token sharing a line with real prose just loses the token.
      .map(function (l) { return String(l).replace(_blockTokenRe, '').replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+$/, ''); });
    while (_bodyLines.length && String(_bodyLines[_bodyLines.length - 1]).trim() === '') _bodyLines.pop();
    /* CUSTOM_ASKCLAUSE_BY_CLAUDE — woven AFTER the tokens are stripped and the trailing blanks
       are popped, so the ask sentence this searches for is the one the rep will actually see. */
    var proseLines = weaveAskClauses(weavePriorDims(weaveParcelAsk(weavePriorityAsk(_bodyLines, shipVia), parcel, itemCount, shipVia), priorDims), extraBodyLines);   // CUSTOM_PRIORDIMS_BY_CLAUDE; weavePriorityAsk: CUSTOM_FEDEXPRIORITYASK_BY_CLAUDE
    return proseLines.map(function (l) {
      var fmt = escHtml(l).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/__(.*?)__/g, '<u>$1</u>').replace(/\*(.*?)\*/g, '<em>$1</em>'); // __u__: CUSTOM_FEDEXPRIORITYASK_BY_CLAUDE
      return '<p>' + (fmt || '<br>') + '</p>';
    }).join('');
  }

  /* ⛔ THE SEAM. This is what "the agent shares the button's composer" MEANS: the whole email —
     subject, recipients and body HTML — from a deal reference, a vendor and an ask type, with no
     canvas and nobody typing. The worker's twin is what an AI agent calls; nothing here reads the
     DOM, so both runtimes get the same answer or the parity test goes red.

     ⚠️ IT IS A COMPOSITION OF THE FUNCTIONS ABOVE AND ADDS NO RULE OF ITS OWN. That is deliberate
     and it is the whole guarantee: every step below is a line open() or sendOverride() also runs,
     so "the seam agrees with the twin" and "the seam agrees with the button" are the same
     statement. A rule invented here would be true of the agent's email and of nobody else's. */
  function buildFreightRequestEmail(args) {
    args = args || {};
    var emailType = args.emailType || 'freight';
    var vars = freightVarsFor(args.vendorName, args.zip, args.quoteRef);
    var tpl = freightTemplateFor(args.vendorFreight, emailType);
    var itemsHtml = buildItemsTableHtml(Array.isArray(args.items) ? args.items : [], { editable: false, vendorName: args.vendorName });
    // Dims asks carry neither block: the supplier is being asked to weigh and measure, not to
    // price a delivery. Same two conditions open() applies.
    var shipToHtml = (emailType !== 'dims') ? buildShipToTableHtml(args.shipToParts, args.shipVia) : '';
    var addOnsHtml = (emailType !== 'dims') ? buildAddOnsInnerHtml(args.addOnsLines) : '';
    /* CUSTOM_PRIORDIMS_BY_CLAUDE — a freight ask only; a dims or both ask is never re-worded. */
    var prefillBody = buildPrefillBodyHtml(tpl.body, vars, args.extraBodyLines, emailType === 'freight' ? args.priorDims : null, emailType !== 'dims' ? args.shipVia : null, emailType !== 'dims' ? (args.parcel || null) : null, Array.isArray(args.items) ? args.items.length : 0); // shipVia: CUSTOM_FEDEXPRIORITYASK_BY_CLAUDE; parcel: CUSTOM_PARCELASK_BY_CLAUDE
    return {
      subject: renderFreightVars(tpl.subject, vars),
      // postHtml is '' — that is the note a rep types UNDER the items table, and an agent has no
      // canvas to type it in. Passed explicitly so the absence is a decision, not an omission.
      html: composeFromProse(prefillBody, itemsHtml, '', args.quoteRef, args.signatureHtml, shipToHtml, addOnsHtml),
      recipients: mergeVendorRecipients(args.vendorRecipients, args.vendorFreight, emailType),
      prefillBody: prefillBody,
      itemsHtml: itemsHtml,
      shipToHtml: shipToHtml,
      addOnsHtml: addOnsHtml,
    };
  }

  /* What the parity test pulls out of this block. Not exposed on `window` — the extractor
     evaluates the span and reads this name, so nothing about the page changes. */
  var FreightComposeTwin = {
    escHtml: escHtml,
    shortenVendorName: shortenVendorName,
    renderFreightVars: renderFreightVars,
    buildItemsTableHtml: buildItemsTableHtml,
    buildShipToTableHtml: buildShipToTableHtml,
    buildAddOnsInnerHtml: buildAddOnsInnerHtml,
    freightCard: freightCard,
    substituteFreightBlocks: substituteFreightBlocks,
    normalizeQuillMargins: normalizeQuillMargins,
    refFooterHtml: refFooterHtml,
    emailSignatureHtml: emailSignatureHtml,
    composeFromProse: composeFromProse,
    mergeVendorRecipients: mergeVendorRecipients,
    freightVarsFor: freightVarsFor,
    freightTemplateFor: freightTemplateFor,
    buildPrefillBodyHtml: buildPrefillBodyHtml,
    isAskLine: isAskLine,
    clauseCase: clauseCase,
    weaveAskClauses: weaveAskClauses,
    weavePriorDims: weavePriorDims,   // CUSTOM_PRIORDIMS_BY_CLAUDE
    isFedexPriorityShipVia: isFedexPriorityShipVia,   // CUSTOM_FEDEXPRIORITYASK_BY_CLAUDE
    weavePriorityAsk: weavePriorityAsk,
    weaveParcelAsk: weaveParcelAsk,   // CUSTOM_PARCELASK_BY_CLAUDE
    parcelAskSentence: parcelAskSentence,
    FEDEX_PRIORITY_ASK: FEDEX_PRIORITY_ASK,
    buildFreightRequestEmail: buildFreightRequestEmail,
    FREIGHT_DEFAULTS: FREIGHT_DEFAULTS,
  };
  /* FREIGHT-COMPOSE-TWIN:END */

  /* CUSTOM_FREIGHTTWIN_BY_CLAUDE (AIOPS suggestion 6743fdf255d8, Ryan 2026-09-03) — the two
     helpers below sat INSIDE what is now the FREIGHT-COMPOSE-TWIN span. They reach for
     sessionStorage and for the DOM, so the parity test could not evaluate the block with them
     in it. Moved here verbatim — no logic changed, and they are the composer's only impure
     helpers, which is why the span above is worth having. */
  function repSessionEmail() {
    try { var raw = sessionStorage.getItem('rep_portal_session'); return raw ? ((JSON.parse(raw) || {}).email || '').trim() : ''; } catch (_) { return ''; }
  }
  function wireItemEdits(panel, items) {
    panel.querySelectorAll('[data-item-idx][data-field]').forEach(function (el) {
      el.addEventListener('input', function () {
        var idx = parseInt(el.getAttribute('data-item-idx'), 10);
        var field = el.getAttribute('data-field');
        if (isNaN(idx) || !field || !items[idx]) return;
        items[idx][field] = (el.textContent || '').trim();
      });
      el.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); el.blur(); } });
    });
    /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — sub-description chips. Clicking one
       flips its `included` flag, rebuilds that row's `subLines` from the surviving choices, and
       re-renders ONLY that row's cluster.

       ⛔ ONE DELEGATED LISTENER ON THE PANEL, not one per chip: the re-render below replaces the
       chip elements, so per-chip listeners would die on the first click and the second toggle
       would do nothing.

       ⚠️ Re-rendering just the cluster rather than the whole table is deliberate — rebuilding the
       table would blow away the caret in whatever qty/SKU cell the rep was mid-edit in. */
    panel.addEventListener('click', function (e) {
      var chip = e.target && e.target.closest && e.target.closest('.rq-ws-email-modal__subdesc-toggle');
      if (!chip || !panel.contains(chip)) return;
      var itemIdx = parseInt(chip.getAttribute('data-item-idx'), 10);
      var sdIdx = parseInt(chip.getAttribute('data-sd-idx'), 10);
      if (isNaN(itemIdx) || isNaN(sdIdx)) return;
      var row = items[itemIdx];
      if (!row || !row.subDescChoices || !row.subDescChoices[sdIdx]) return;
      row.subDescChoices[sdIdx].included = !row.subDescChoices[sdIdx].included;
      row.subLines = row.subDescChoices
        .filter(function (c) { return c.included; })
        .map(function (c) { return c.text; });
      var wrap = chip.parentNode;
      if (wrap) {
        wrap.innerHTML = row.subDescChoices.map(function (c, j) {
          return subDescChipHtml(itemIdx, j, c);
        }).join('');
      }
    });
    /* END CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE */
  }

  function open(opts) {
    state = opts || {};
    if (!window.RQD || typeof window.RQD.openComposeModal !== 'function') {
      alert('Composer not loaded yet — refresh the page.'); return;
    }
    if (!API) API = (window.RepPortal && window.RepPortal.API) || null;
    var emailType = state.type || 'freight';
    var rows = Array.isArray(state.items) ? state.items : [];
    var shipTo = state.shipTo || '';
    var rec = mergeVendorRecipients(state.vendorRecipients, state.vendorFreight, emailType);
    var toStr = (rec.email && Array.isArray(rec.email)) ? rec.email.join(', ') : (rec.email || '');
    var ccArr = (rec.cc && Array.isArray(rec.cc)) ? rec.cc.slice() : (rec.cc ? String(rec.cc).split(',').map(function(s){ return s.trim(); }).filter(Boolean) : []);
    var bccArr = (rec.bcc && Array.isArray(rec.bcc)) ? rec.bcc.slice() : (rec.bcc ? String(rec.bcc).split(',').map(function(s){ return s.trim(); }).filter(Boolean) : []);

    /* CUSTOM_RFQPORTAL_BY_CLAUDE — per-vendor freight verbiage (Edit Vendor → PO
       Profile). Empty = the gold-standard built-in below. The custom body is the
       FULL message (incl. its own greeting); {VENDOR}/{ZIP}/{REF} resolve in both
       subject and body, and the {…_BLOCK} tokens place the cards.
       (Ryan 2026-07-22) Built-ins are vendor-agnostic and identical to the ones in
       workstation-freight.js, so QW and DD send the same email for the same deal. */
    /* CUSTOM_FREIGHTTWIN_BY_CLAUDE (Ryan 2026-09-03) — the pick and the substitutions moved into
       freightTemplateFor / freightVarsFor above so the worker can run the same two steps. Same
       code, one level out; the behaviour is unchanged and the parity test is what keeps it so. */
    var fvVars = freightVarsFor(state.vendorName, state.zip, state.quoteRef);
    var fvTpl = freightTemplateFor(state.vendorFreight, emailType);
    var subject = renderFreightVars(fvTpl.subject, fvVars);

    // CUSTOM_RFQPORTAL_BY_CLAUDE — SHIP TO + SHIP VIA render as a neat table block (freight
    // asks only), injected like the items table and non-editable; no longer prose lines.
    var shipToHtml = (emailType !== 'dims') ? buildShipToTableHtml(state.shipToParts, state.shipVia) : '';
    // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — ADD ONS rides as its own block
    // so {ADD_ONS_BLOCK} resolves. Dims asks never carry it (the supplier only needs
    // to weigh and measure), and it collapses to nothing when no condition is active.
    var addOnsHtml = (emailType !== 'dims') ? buildAddOnsInnerHtml(state.addOnsLines) : '';

    /* CUSTOM_FREIGHTTWIN_BY_CLAUDE (Ryan 2026-09-03) — the Quill seed is built by
       buildPrefillBodyHtml above. Lifted verbatim; its comments went with it. This is the string
       an agent's email composes from directly, which is why it had to leave open(). */
    var prefillBody = buildPrefillBodyHtml(fvTpl.body, fvVars, state.extraBodyLines, emailType === 'freight' ? state.priorDims : null, emailType !== 'dims' ? state.shipVia : null, emailType !== 'dims' ? (state.parcel || null) : null, Array.isArray(state.items) ? state.items.length : 0);   // CUSTOM_PRIORDIMS_BY_CLAUDE; shipVia: CUSTOM_FEDEXPRIORITYASK_BY_CLAUDE; parcel: CUSTOM_PARCELASK_BY_CLAUDE

    // Mutable copy of items; the preStep's qty edits write here, send + preview read it.
    /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — `_vendor` and `subDescChoices` ride
       along now. ⛔ THE COPY IS WHAT THE TABLE RENDERS FROM, so a field dropped here is a feature
       that silently does not exist: without subDescChoices every chip cluster renders empty and
       the toggles have nothing to flip; without _vendor a multi-vendor QW ask loses its per-row
       supplier prefix. The choices are DEEP-copied — toggling must not write back through to the
       caller's row objects, which the Workstation keeps for its own panel. */
    var currentItems = rows.map(function (r) {
      return {
        qtyStr: r.qtyStr, title: r.title, sku: r.sku,
        subLines: (r.subLines || []).slice(),
        _vendor: r._vendor || '',
        subDescChoices: Array.isArray(r.subDescChoices)
          ? r.subDescChoices.map(function (c) { return { text: c.text, included: !!c.included }; })
          : undefined,
      };
    });
    // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — items render EDITABLE directly in the
    // white canvas (no separate preStep — it was redundant): qty/sku/title edits flow into
    // currentItems → the sent email. The clean (non-editable) table is what actually sends
    // (built in sendOverride). Cells are wired just after the modal opens (below).
    window.RQD.openComposeModal({
      ref: state.quoteRef || '',
      prefillTo: toStr,
      prefillCc: ccArr,
      prefillBcc: bccArr,
      prefillSubject: subject,
      prefillBody: prefillBody,
      suppressFollowUp: true,
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15) — From: selector. 'freight' remembers
         (per rep) whether freight requests send from Ryan Walker <ryan@> or Abby <orders@>.
         The dropdown owns the signature preview (rep/Abby from rep-settings); the sent email
         uses that same signature via args.fromSignatureHtml below — preview == sent. */
      emailType: 'freight',
      /* CUSTOM_VENDORFROM_BY_CLAUDE (Ryan 2026-08-27) — the vendor's "Emails Send From" rule now
         seeds this composer too, not just the PO one. Ryan set TKO to Abby, opened the freight
         request on RW-1339-A and watched it default to ryan@: *"I basically want the PO SENDS FROM
         to include freight requests and weight/dims requests too."*

         ⛔ ONE FUNCTION, SHARED WITH THE PO COMPOSER — `RPDash.defaultFromMailboxFor`. A local copy
         of "who is this from" is exactly how a Legend PO went out as Ryan before the rule moved
         onto the vendor profile. It resolves a white-label row to its real supplier internally, so
         a Blue Sky Fitness Supply row asks Ferus Prime's setting rather than answering for us.

         `forceFrom` beats the rep's saved per-type default and does NOT write itself back as a new
         default (see openComposeModal's header) — a vendor rule must not become the rep's habit for
         every other vendor's freight requests. Still switchable in the dropdown; blank here means
         no rule and today's behaviour is unchanged.

         Covers weight/dims requests for free: this same modal serves both kinds. */
      /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — ⛔ THE PROFILE ANSWERS FIRST NOW,
         and it has to. `RPDash.defaultFromMailboxFor` lives in dashboard-main.js, which the Quote
         Workstation does not load — so on the QW that guard was simply false and EVERY vendor's
         "Emails Send From" rule would have silently reverted to the rep's habit. Exactly the shape
         of the TKO miss on 2026-08-27: a rule that reaches nothing, failing as a default rather
         than as an error.

         `vendorFreight.poFrom` comes from /vendor-freight-email, which resolves a white-label row
         to its real supplier server-side (resolvePoVendorName) — a better answer than the local
         map, not just an available one. RPDash stays as the fallback for the Deal Dashboard when
         that fetch fails, which is the case the theme-side list was kept alive to cover. */
      forceFrom: ((state.vendorFreight && state.vendorFreight.poFrom) || '')
        || ((window.RPDash && window.RPDash.defaultFromMailboxFor)
            ? window.RPDash.defaultFromMailboxFor(state.quoteRef || '', state.vendorName)
            : ''),
      fromRepLabel: 'Ryan Walker <ryan@blueskyfitsupply.com>',
      /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-28) — the ⏱ "business hours only" box on both
         surfaces. /freight-email honours it by queueing the rendered email; see the hold in
         vendor-email-routes.js. Adding the box without that hold is the failure this codebase
         already has a monument to (pending-rep-email.js).

         ⛔ UNCHECKED BY DEFAULT (Ryan 2026-08-29). It shipped ticked, on the reasoning that mail
         should not land at 2am unless somebody says so — which is a CUSTOMER argument, and this
         email goes to a SUPPLIER. Ryan: *"only automatically checked on emails going to customers,
         but unchecked on emails going to vendors (PO's, freight or weight/dim requests, etc.)."*

         The asymmetry is real rather than a preference: a customer reading a 2am email forms an
         impression of the company, while a supplier's shared orders@ inbox is worked as a queue in
         the morning regardless of when it arrived. Holding the ask only delays the answer we are
         waiting on — and a weight/dims request is usually the thing blocking the PO. */
      showBusinessHours: true,
      businessHoursDefault: false,
      /* CUSTOM_VENDORFROM_BY_CLAUDE (Ryan 2026-08-27) - ⛔ THE HARDCODED SELF-BCC IS GONE.
         It pre-filled ryan@ into the visible Bcc whenever the email sent AS Ryan, because these go
         out through Resend and never land in his Outlook Sent folder. Ryan: *"I just realized we
         can set this on all vendors in the vendor guide as we need to, so we don't need that hard
         coded rule anymore."*

         The Guide's PO Profile -> Bcc is now the single source of who gets a copy, and it already
         prefills this composer, so one field decides instead of a field plus a rule that silently
         appended to it.

         ⚠️ MEASURED BEFORE REMOVING: 13 of 41 vendor profiles carry a Bcc today (all ryan@); the
         other 28 carry none, and nothing else adds one - `/freight-email` sends exactly the list it
         is given and `handleEmailPo` falls back only to the vendor's own configured Bcc. So on
         those 28, a vendor email now goes out with NO copy to anybody until the profile is filled
         in. That is the intended trade, recorded here because the failure is silent. */
      previewBlocks: (shipToHtml ? ['<strong>SHIP TO:</strong><br>' + shipToHtml] : []).concat([
        '<strong>ITEMS:</strong><br>' + buildItemsTableHtml(currentItems, { editable: true, vendorName: state.vendorName }),
        '<div class="rqd-fr-postnote" contenteditable="true" spellcheck="true"></div>'
      ]),
      sendOverride: function (args) {
        var _pn = document.querySelector('#rqd-email-modal .rqd-fr-postnote');
        var postHtml = (_pn && (_pn.textContent || '').trim()) ? _pn.innerHTML : '';
        var html = composeFromProse(args.bodyHtml, buildItemsTableHtml(currentItems, { editable: false, vendorName: state.vendorName }), postHtml, state.quoteRef, args.fromSignatureHtml, shipToHtml, addOnsHtml);
        var payload = {
          /* WARNING - 'both' IS SENT AS 'freight', AND THAT IS THE WHOLE DESIGN. `type` reaches
             /freight-email, which throws on anything outside ['freight','dims'], and
             freightQuotes[].kind is read as a strict binary in ~20 places across six theme and
             six worker modules - three of which REJECT an unknown value rather than defaulting
             (the shipping card's `hasRequestRow` simply goes false and the request row
             disappears). So a 'both' send stamps the freight kind exactly as it always did and
             records the dims half in its own field via `alsoDims`. Every existing reader keeps
             working untouched, and requestedAt being ONE timestamp is why a third enum could
             never have named both asks anyway. */
          type: emailType === 'both' ? 'freight' : emailType,
          alsoDims: emailType === 'both',
          vendorName: state.vendorName,
          toEmail: (args.to || []).join(', '),
          subject: args.subject,
          html: html,
          quoteRef: state.quoteRef,
          fromMailbox: args.fromMailbox || 'REP',
          highImportance: !!args.highImportance,
          // CUSTOM_SENDWINDOW_BY_CLAUDE — worker queues the send when outside the window.
          businessHoursOnly: args.businessHoursOnly === true,
        };
        if ((args.cc || []).length) payload.cc = args.cc.join(', ');
        /* CUSTOM_VENDORFROM_BY_CLAUDE (2026-08-29) — ⛔ ALWAYS post `bcc`, even empty. The
           worker's per-vendor memory write-back is guarded on `body.bcc !== undefined`
           (vendor-email-routes.js), so omitting the key on an emptied box left the previous
           value in `vendor-contacts` forever — and that value then pre-filled the next email
           and overrode a Guide profile carrying no Bcc. It is what made the write-back's own
           "Send once without it, and the next pre-fill is empty. Self-correcting." comment
           false on this path. An empty string is safe end to end: the worker's parseEmails('')
           returns undefined, exactly as omitting it did, so nothing is added to the send.
           `cc` stays conditional on purpose — a remembered Cc is ADDITIVE by design (see
           mergeVendorRecipients), so an empty Cc box must not erase somebody a human put there. */
        payload.bcc = (args.bcc || []).join(', ');
        if (state.bucket) payload.bucket = state.bucket;
        if (args.isTest) {
          delete payload.quoteRef;           // test → no freight-handshake stamp
          var me = repSessionEmail();
          if (me) payload.toEmail = me;
          payload.subject = '[TEST] ' + payload.subject;
        }
        if (!API) API = (window.RepPortal && window.RepPortal.API) || null;
        return API.post('/freight-email', payload).then(function (resp) {
          if (resp && resp.success === false) throw new Error((resp && resp.error) || 'Send failed');
          if (!args.isTest && typeof state.onSent === 'function') {
            /* CUSTOM_SENDWINDOW_BY_CLAUDE (Ryan 2026-08-28) — a HELD send still stamps the row.
               Ryan's call: the ask is committed the moment Send is clicked, so the vendor reads
               "Requested" straight away and nobody asks the same supplier twice while the email
               sits in the queue. `scheduledFor` rides along so the surface can say when it goes.
               The worker writes the same two fields to KV, so the optimistic view and the
               refetched one agree. */
            try {
              state.onSent({
                quoteRef: state.quoteRef,
                vendorName: state.vendorName,
                requestedAt: new Date().toISOString(),
                scheduledFor: (resp && resp.scheduled) ? (resp.sendAfter || null) : null,
              });
            } catch (_) {}
          }
          /* CUSTOM_SENTITEMS_BY_CLAUDE (Ryan 2026-08-28) — qualify the composer's own success when
             the send fell back to Resend, so "it is not in Sent Items" is stated at the moment of
             sending rather than found later. Empty on the Graph path. */
          var _note = (window.RQD && window.RQD.transportNote) ? window.RQD.transportNote(resp) : '';
          return _note ? { composerNote: _note } : resp;
        });
      }
    });

    // The modal is built synchronously, so the editable items table is now in the canvas —
    // wire its cells so qty/sku/title edits write back into currentItems.
    var _ov = document.getElementById('rqd-email-modal');
    /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — ⛔ BUG FIX, found porting the chips.
       This read `.querySelector('.rqd-email-modal__compose-preview-block')` — SINGULAR, first match.
       Every previewBlock gets its own element of that class (dashboard-email-modal.js
       _installPreviewBlocks), and a FREIGHT ask passes SHIP TO as block #1 and the items table as
       block #2. So the edit listeners were being attached to the ship-to table, and qty / SKU /
       title edits on a freight-quote request silently did nothing — the email went out with the
       original quantities. A weight-dims ask has no SHIP TO block, which is why the same control
       worked there and the bug stayed invisible.

       Wiring the HOST (plural class) covers every block, whatever order the caller passes them in,
       and is what the delegated chip listener needs anyway. */
    var _pb = _ov && _ov.querySelector('.rqd-email-modal__compose-preview-blocks');
    if (_pb) wireItemEdits(_pb, currentItems);
  }

  window.DashboardFreightModal = { open: open };
})();
